import { useState } from 'react'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import * as api from '../../../services/api'
import ModalDelete from '../../../components/ModalDelete'
import { extractApiErrorMessage } from '../../../utils/extractApiErrorMessage'
import { CompanyApiFailureMessageProps, FailureRule } from '../../../types/companyApiFailureMessage'
import FailureRuleBuilder, { describeRule } from '../../../components/FailureRuleBuilder'

type FailureMessageListProps = {
  items: CompanyApiFailureMessageProps[]
  onRulesChange: (itemId: string, rules: FailureRule[]) => void
  onDeleted: (itemId: string) => void
}

export default function FailureMessageList({ items, onRulesChange, onDeleted }: FailureMessageListProps): JSX.Element {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<boolean>(false)

  function toggleExpanded(itemId: string, isExpanded: boolean) {
    setExpandedId(isExpanded ? itemId : null)
  }

  async function saveItem(item: CompanyApiFailureMessageProps) {
    setSavingId(item.id)
    const response = await api.put(
      `company/api-failure-message/update/${item.id}/`,
      { rules: item.rules }
    ) as { [key: string]: any }
    setSavingId(null)

    if (response.status === 200) {
      toast.success("Regras atualizadas com sucesso!", {
        position: toast.POSITION.TOP_RIGHT
      })
    } else {
      toast.error(extractApiErrorMessage(response), {
        position: toast.POSITION.TOP_RIGHT
      })
    }
  }

  async function confirmDelete() {
    setDeleting(true)
    const response = await api.destroy(`company/api-failure-message/destroy/${deleteId}/`) as { [key: string]: any }
    setDeleting(false)

    if (response.status === 204) {
      onDeleted(deleteId!)
      toast.success("Mensagem de erro excluída com sucesso!", {
        position: toast.POSITION.TOP_RIGHT
      })
    } else {
      toast.error("Não foi possível excluir essa configuração.", {
        position: toast.POSITION.TOP_RIGHT
      })
    }
    setDeleteId(null)
  }

  return (
    <>
      {items.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ marginBottom: 2, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.85rem' }}>
            Mensagens cadastradas
          </Typography>
          {
            items.map((item) => {
              const isExpanded = expandedId === item.id
              const ruleDescriptions = item.rules.map((rule) => describeRule(rule))

              return (
                <Accordion
                  key={`failure-message-${item.id}`}
                  expanded={isExpanded}
                  onChange={(_, expanded) => toggleExpanded(item.id, expanded)}
                  disableGutters
                  sx={{ marginBottom: 2 }}
                >
                  <AccordionSummary sx={{ paddingY: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Typography variant='body1' fontWeight={600}>{item.api_name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                          <Typography variant="button" sx={{ fontSize: '0.75rem', lineHeight: 1 }}>Expandir</Typography>
                          <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </Box>
                      </Box>
                      {
                        !isExpanded && (
                          <Box sx={{ position: 'relative', maxHeight: 70, overflow: 'hidden', marginTop: 1 }}>
                            {ruleDescriptions.map((description, index) => (
                              <Typography key={index} variant="body2" sx={{ color: 'text.secondary' }}>
                                {description}
                              </Typography>
                            ))}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: 40,
                                background: (theme) => `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`
                              }}
                            />
                          </Box>
                        )
                      }
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FailureRuleBuilder
                      rules={item.rules}
                      onChange={(rules) => onRulesChange(item.id, rules)}
                    />
                    <Box sx={{ display: 'flex', gap: 1, marginTop: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={savingId === item.id}
                        onClick={() => saveItem(item)}
                      >
                        Salvar
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setDeleteId(item.id)}
                      >
                        Excluir
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )
            })
          }
        </>
      )}

      <ModalDelete
        open={!!deleteId}
        textBody="Tem certeza que deseja excluir essa mensagem de erro personalizada?"
        handleModalClose={() => setDeleteId(null)}
        handleDelete={confirmDelete}
        loading={deleting}
      />
    </>
  )
}
